import { Box } from "@primer/react";
import { ChevronUpIcon, ChevronDownIcon } from '@primer/octicons-react';
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Pie } from "react-chartjs-2";
import { DashBoardModel } from "../../models/response/thu-phi/DashBoardModel";
import { phanBoPhiApi } from "../../api/phanBoPhiApi";
import { useSelector } from "react-redux";
import { RootState } from "../../state/reducers";

// Đăng ký các thành phần cho biểu đồ
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const ChartDoanhThuTheoHinhThucThanhToan = () => {
  const [dashBoardModel, setDashBoardModel] = useState<DashBoardModel[]>([]);
  const [isDetailCollapsed, setIsDetailCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { dm_coso_id, nam_hoc } = useSelector(
    (state: RootState) => state.common
  );

  useEffect(() => {
    const handleReloadDashBoard = async () => {
      try {
        setIsLoading(true);
        const res = await phanBoPhiApi.SelectDashBoard({
          dm_coso_id,
          nam_hoc,
        });
        if (res.is_success) {
          setDashBoardModel(res.data);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    handleReloadDashBoard();
  }, [dm_coso_id, nam_hoc]);

  // Tính tổng doanh thu theo từng hình thức thanh toán
  const dataSource = useMemo(() => {
    // Group data theo hinh_thuc_thanh_toan và tính tổng so_tien
    const groupedData = dashBoardModel.reduce((acc, item) => {
      const existingHinhThuc = acc.find(x => x.hinh_thuc_thanh_toan === item.hinh_thuc_thanh_toan);
      if (existingHinhThuc) {
        existingHinhThuc.so_tien += item.so_tien;
      } else {
        acc.push({
          hinh_thuc_thanh_toan: item.hinh_thuc_thanh_toan,
          sf_hinhthucthanhtoan_id: item.sf_hinhthucthanhtoan_id,
          so_tien: item.so_tien,
        });
      }
      return acc;
    }, [] as Array<{ hinh_thuc_thanh_toan: string; sf_hinhthucthanhtoan_id: number; so_tien: number }>);

    // Sắp xếp theo số tiền giảm dần
    return groupedData.sort((a, b) => b.so_tien - a.so_tien);
  }, [dashBoardModel]);

  // Format số tiền thành VND
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Màu sắc cho biểu đồ tròn
  const colors = [
    "#182D62", // Màu chính
    "#006BFF", // Xanh dương
    "#28A745", // Xanh lá
    "#FFC107", // Vàng
    "#DC3545", // Đỏ
    "#6F42C1", // Tím
    "#FD7E14", // Cam
    "#20C997", // Xanh ngọc
    "#6C757D", // Xám
    "#E83E8C", // Hồng
  ];

  // Chuẩn bị dữ liệu cho Pie Chart
  const data = {
    labels: dataSource.map((item) => item.hinh_thuc_thanh_toan || "Không xác định"),
    datasets: [
      {
        label: "Doanh thu (VND)",
        data: dataSource.map((item) => item.so_tien),
        backgroundColor: colors.slice(0, dataSource.length),
        borderColor: "#fff",
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverBorderColor: "#182D62",
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            size: 12,
            weight: 'bold',
          },
          color: '#182D62',
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      title: {
        display: true,
        text: "Phân bố doanh thu theo hình thức thanh toán",
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#182D62',
        padding: 20,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#182D62',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            const total = context.dataset.data.reduce((sum: number, value: number) => sum + value, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${formatCurrency(context.parsed)} (${percentage}%)`;
          }
        }
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
    },
  };

  // Component Loading
  const LoadingComponent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "400px",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #006bff",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          "@keyframes spin": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
          },
        }}
      />
      <Box
        sx={{
          fontSize: "1rem",
          color: "#666",
          fontWeight: 500,
        }}
      >
        Đang tải dữ liệu...
      </Box>
    </Box>
  );

  // Nếu đang loading, hiển thị loading component
  if (isLoading) {
    return (
      <Box sx={{
        padding: 3,
        backgroundColor: '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        height: '100%'
      }}>
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <Box sx={{
      padding: 3,
      backgroundColor: '#fff',
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      height: '100%'
    }}>
      <Box sx={{
        marginBottom: 3,
        textAlign: 'center',
        borderBottom: '1px solid #ddd',
        paddingBottom: 2
      }}>
        <Box sx={{
          fontSize: 18,
          fontWeight: 600,
          color: '#666',
          marginBottom: 1
        }}>
          Hình thức Thanh toán
        </Box>
        <Box sx={{ 
          fontSize: "1.1rem", 
          color: "fg.muted",
          fontStyle: 'italic'
        }}>
          Phân bố doanh thu theo các hình thức thanh toán
        </Box>
        {dataSource.length > 0 && (
          <Box sx={{ 
            fontSize: "0.9rem", 
            color: "#666",
            marginTop: 1
          }}>
            Tổng cộng: {dataSource.length} hình thức | 
            Tổng doanh thu: {formatCurrency(dataSource.reduce((sum, item) => sum + item.so_tien, 0))}
          </Box>
        )}
      </Box>
      
      <Box sx={{ 
        height: 400, 
        width: "100%",
        position: 'relative'
      }}>
        {dataSource.length > 0 ? (
          <Pie data={data} options={options} />
        ) : (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            fontSize: '1.2rem',
            color: '#666',
            fontStyle: 'italic'
          }}>
            🥧 Không có dữ liệu để hiển thị
          </Box>
        )}
      </Box>

      {/* Bảng thống kê chi tiết với collapse */}
      {dataSource.length > 0 && (
        <Box sx={{ marginTop: 3 }}>
          <Box
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: '#182D62',
              marginBottom: 2,
              textAlign: 'center',
              cursor: 'pointer',
              padding: 1,
              borderRadius: 1,
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: '#f8f9fa'
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
            onClick={() => setIsDetailCollapsed(!isDetailCollapsed)}
          >
            {isDetailCollapsed ? (
              <ChevronDownIcon size={16} />
            ) : (
              <ChevronUpIcon size={16} />
            )}
            Chi tiết theo hình thức
          </Box>

          {!isDetailCollapsed && (
            <Box sx={{
              display: 'grid',
              gap: 1,
              fontSize: '0.9rem',
              animation: 'fadeIn 0.3s ease-in-out',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(-10px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
              }
            }}>
              {dataSource.map((item, index) => {
                const total = dataSource.reduce((sum, i) => sum + i.so_tien, 0);
                const percentage = ((item.so_tien / total) * 100).toFixed(1);
                return (
                  <Box key={index} sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 1,
                    backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'transparent',
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#e9ecef'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: colors[index],
                        borderRadius: '50%'
                      }} />
                      <Box sx={{ fontWeight: 500 }}>
                        {item.hinh_thuc_thanh_toan || "Không xác định"}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ fontWeight: 600, color: '#182D62' }}>
                        {formatCurrency(item.so_tien)}
                      </Box>
                      <Box sx={{ fontSize: '0.8rem', color: '#666' }}>
                        {percentage}%
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ChartDoanhThuTheoHinhThucThanhToan;
